import React, { useState, useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, formatDate } from '../utils/api';

const Dashboard = ({ admin, onNavigate }) => {
  const { print_lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await apiGet('/api/admin/stats');
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error(err.message || 'Failed to fetch stats', err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = () => {
    if (!admin.license_expired) return null;
    const expiry = new Date(admin.license_expired);
    const today = new Date();
    const diff = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysRemaining = getDaysRemaining();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          {print_lang('dashboard.welcome', 'Welcome')}, {admin.name}!
          {admin.is_super_admin && <span className="super-admin-badge">Super Admin</span>}
        </h1>
      </div>

      {/* License Warning */}
      {daysRemaining !== null && daysRemaining <= 30 && (
        <div className={`alert ${daysRemaining <= 7 ? 'alert-error' : 'alert-warning'}`} style={{ marginBottom: '1.5rem' }}>
          <strong>{print_lang('dashboard.licenseExpiry', 'License Expiry')}:</strong>{' '}
          {formatDate(admin.license_expired)} ({daysRemaining} {print_lang('dashboard.daysRemaining', 'days remaining')})
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        {/* Branches */}
        <div className="stat-card">
          <div className="stat-card-icon brand">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div className="stat-card-value">{stats?.branch_count ?? 0}</div>
          <div className="stat-card-label">{print_lang('dashboard.totalBranches', 'Total Branches')}</div>
        </div>

        {/* Users */}
        <div className="stat-card">
          <div className="stat-card-icon blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="stat-card-value">{stats?.user_count ?? 0}</div>
          <div className="stat-card-label">{print_lang('dashboard.totalUsers', 'Total Users')}</div>
        </div>

        {/* Dentists */}
        <div className="stat-card">
          <div className="stat-card-icon green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </div>
          <div className="stat-card-value">{stats?.dentist_count ?? 0}</div>
          <div className="stat-card-label">{print_lang('dashboard.totalDentists', 'Total Dentists')}</div>
        </div>

        {/* Patients */}
        <div className="stat-card">
          <div className="stat-card-icon orange">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div className="stat-card-value">{stats?.patient_count ?? 0}</div>
          <div className="stat-card-label">{print_lang('dashboard.totalPatients', 'Total Patients')}</div>
        </div>

        {/* Admins - Super Admin Only */}
        {admin.is_super_admin && (
          <div className="stat-card">
            <div className="stat-card-icon brand">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div className="stat-card-value">{stats?.admin_count ?? 0}</div>
            <div className="stat-card-label">{print_lang('dashboard.totalAdmins', 'Total Admins')}</div>
          </div>
        )}

        {/* Days Remaining */}
        <div className="stat-card">
          <div className="stat-card-icon" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </div>
          <div className="stat-card-value">
            {daysRemaining !== null ? (
              <span style={{ color: daysRemaining <= 7 ? '#ef4444' : daysRemaining <= 30 ? '#f59e0b' : '#10b981' }}>
                {daysRemaining > 0 ? daysRemaining : 0}
              </span>
            ) : '-'}
          </div>
          <div className="stat-card-label">{print_lang('dashboard.daysRemaining', 'Days Remaining')}</div>
        </div>
      </div>

      {/* Super Admin: Pending Admins Alert */}
      {admin.is_super_admin && stats?.pending_admins > 0 && (
        <div className="alert alert-warning" style={{ marginTop: '1.5rem' }}>
          <strong>{stats.pending_admins}</strong> {print_lang('dashboard.pendingAdmins', 'admin(s) pending verification')}
        </div>
      )}

      {/* Quick Info Cards */}
      <div className="grid-2" style={{ marginTop: '1.5rem' }}>
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>
            {print_lang('dashboard.accountInfo', 'Account Information')}
          </h3>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{print_lang('admin.name', 'Name')}:</strong> {admin.name}
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{print_lang('admin.email', 'Email')}:</strong> {admin.email}
            </p>
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>{print_lang('admin.status', 'Status')}:</strong>{' '}
              <span className={`badge ${admin.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                {admin.status === 'active' ? print_lang('common.active', 'Active') : admin.status}
              </span>
            </p>
            <p>
              <strong>{print_lang('admin.licenseExpiry', 'License Expiry')}:</strong> {formatDate(admin.license_expired)}
            </p>
          </div>
        </div>

        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>
            {print_lang('dashboard.quickActions', 'Quick Actions')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              className="btn btn-outline"
              style={{ justifyContent: 'flex-start' }}
              onClick={() => onNavigate && onNavigate('branches')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              {print_lang('dashboard.manageBranches', 'Manage Branches')}
            </button>
            <button
              className="btn btn-outline"
              style={{ justifyContent: 'flex-start' }}
              onClick={() => onNavigate && onNavigate('users')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="8.5" cy="7" r="4" />
                <line x1="20" y1="8" x2="20" y2="14" />
                <line x1="23" y1="11" x2="17" y2="11" />
              </svg>
              {print_lang('dashboard.manageUsers', 'Manage Users')}
            </button>
            {admin.is_super_admin && (
              <button
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => onNavigate && onNavigate('admins')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
                {print_lang('dashboard.manageAdmins', 'Manage Admins')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
